import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../SafeIcon';

const {
  FiZap, FiArrowRight, FiLoader, FiSend, FiRefreshCw,
  FiShoppingBag, FiTrendingUp, FiUser, FiCpu, FiChevronDown,
  FiDollarSign, FiClock, FiTarget
} = FiIcons;

const SYSTEM_PROMPT = `You are SideHustlr Coach, an expert in helping people start simple, profitable commerce-based side hustles.

Your goal is to guide the user step-by-step to discover what they can sell and how much they can realistically make.

RULES:
- Only ask ONE question at a time
- Keep language simple, practical, and action-focused
- Only suggest commerce-based ideas (buying/selling, making/selling, flipping, local selling)
- NO dropshipping
- Focus on fast, realistic ways to make money
- Always adapt based on the user's experience level
- Always aim to get the user to their FIRST SALE

FLOW:

STEP 1: Ask experience level
Ask: "What's your experience with selling or making money online?"
Options:
- Beginner (never sold anything)
- Basic (sold a few things here and there)
- Intermediate (sold consistently before)
- Advanced (running a business already)

Wait for answer.

STEP 2: Ask skills/interests
"What are you good at, or what do you enjoy doing?"

Wait for answer.

STEP 3: Ask budget
"How much can you invest to start? (e.g. R0, R500, R1000+)"

Wait for answer.

STEP 4: Ask time
"How many hours per week can you commit?"

Wait for answer.

STEP 5: Generate result

Based on all answers, output a STRICT JSON block wrapped in <HUSTLE_PLAN> tags like this:

<HUSTLE_PLAN>
{
  "intro": "Here's your personalised hustle plan based on your answers! 🔥",
  "ideas": [
    {
      "name": "Idea Name",
      "emoji": "🧵",
      "why": "Why it fits you in one sentence.",
      "costPerUnit": "R50",
      "sellingPrice": "R150",
      "profitPerUnit": "R100",
      "weeklySales": "10–20 units",
      "weeklyProfit": "R1,000–R2,000",
      "monthlyProfit": "R4,000–R8,000",
      "firstMission": "Source 10 units and sell via WhatsApp within 3 days."
    },
    {
      "name": "Idea 2",
      "emoji": "🎨",
      "why": "Why it fits.",
      "costPerUnit": "R20",
      "sellingPrice": "R80",
      "profitPerUnit": "R60",
      "weeklySales": "15–25 units",
      "weeklyProfit": "R900–R1,500",
      "monthlyProfit": "R3,600–R6,000",
      "firstMission": "Make 5 samples and post on Facebook Marketplace."
    },
    {
      "name": "Idea 3",
      "emoji": "🍱",
      "why": "Why it fits.",
      "costPerUnit": "R30",
      "sellingPrice": "R90",
      "profitPerUnit": "R60",
      "weeklySales": "20 units",
      "weeklyProfit": "R1,200",
      "monthlyProfit": "R4,800",
      "firstMission": "Cook a test batch and sell to 5 neighbours this weekend."
    }
  ],
  "bestOption": "Idea Name",
  "bestReason": "Why this is the best pick for you.",
  "momentumQuestion": "Do you want me to help you sell your first 10 units?"
}
</HUSTLE_PLAN>

After the JSON block, also write a short friendly message (1-2 sentences max).

TONE:
- Motivating but not hypey
- Clear and practical
- Slightly gamified (missions, progress, action)

IMPORTANT:
- Do NOT overwhelm the user
- Do NOT give long theory
- Focus on ACTION and MONEY
- ALWAYS output the JSON plan on STEP 5`;

const INITIAL_MESSAGE = {
  role: 'assistant',
  content: `Hey! 👋 I'm your **SideHustlr Coach** — I help people find simple, profitable things they can sell to make real money.

I'll ask you 4 quick questions, then give you a personalised hustle plan with real numbers.

Let's go! 🚀

**What's your experience with selling or making money online?**

- 🟢 **Beginner** — never sold anything
- 🟡 **Basic** — sold a few things here and there  
- 🟠 **Intermediate** — sold consistently before
- 🔴 **Advanced** — running a business already`,
};

function parseHustlePlan(content) {
  try {
    const match = content.match(/<HUSTLE_PLAN>([\s\S]*?)<\/HUSTLE_PLAN>/);
    if (!match) return null;
    return JSON.parse(match[1].trim());
  } catch {
    return null;
  }
}

function getTextWithoutPlan(content) {
  return content.replace(/<HUSTLE_PLAN>[\s\S]*?<\/HUSTLE_PLAN>/g, '').trim();
}

function formatMessage(text) {
  const lines = text.split('\n');
  return lines.map((line, i) => {
    line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    line = line.replace(/\*(.*?)\*/g, '<em>$1</em>');
    if (line.startsWith('# ')) return <h2 key={i} className="text-xl font-black text-[#c8f542] mt-4 mb-2" dangerouslySetInnerHTML={{ __html: line.slice(2) }} />;
    if (line.startsWith('## ')) return <h3 key={i} className="text-base font-black text-[#c8f542] mt-3 mb-1" dangerouslySetInnerHTML={{ __html: line.slice(3) }} />;
    if (line.startsWith('- ') || line.startsWith('• ')) {
      return (
        <div key={i} className="flex items-start space-x-2 my-0.5">
          <span className="text-[#c8f542] mt-1 shrink-0">•</span>
          <span className="text-gray-200" dangerouslySetInnerHTML={{ __html: line.slice(2) }} />
        </div>
      );
    }
    if (line.trim() === '') return <div key={i} className="h-2" />;
    return <p key={i} className="my-0.5 leading-relaxed text-gray-200" dangerouslySetInnerHTML={{ __html: line }} />;
  });
}

function IdeaCard({ idea, isBest }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border overflow-hidden cursor-pointer transition-all ${
        isBest
          ? 'border-[#c8f542]/60 bg-[#c8f542]/5'
          : 'border-[#2c313a] bg-[#1a1d23]'
      }`}
      onClick={() => setExpanded(e => !e)}
    >
      {/* Card Header */}
      <div className="flex items-center justify-between px-4 py-4">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{idea.emoji}</span>
          <div>
            <div className="flex items-center space-x-2">
              <p className="font-black text-white text-sm">{idea.name}</p>
              {isBest && (
                <span className="text-[9px] font-black uppercase tracking-widest bg-[#c8f542] text-black px-2 py-0.5 rounded-full">
                  ⭐ Best Pick
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{idea.why}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3 shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-[10px] text-gray-500 uppercase tracking-widest">Monthly</p>
            <p className="text-sm font-black text-[#c8f542]">{idea.monthlyProfit}</p>
          </div>
          <SafeIcon
            icon={FiChevronDown}
            className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Expanded Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className={`border-t px-4 py-4 space-y-4 ${isBest ? 'border-[#c8f542]/20' : 'border-[#2c313a]'}`}>
              {/* Pricing Row */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Cost/unit', value: idea.costPerUnit, icon: FiDollarSign, color: 'text-red-400' },
                  { label: 'Sell price', value: idea.sellingPrice, icon: FiTrendingUp, color: 'text-blue-400' },
                  { label: 'Profit/unit', value: idea.profitPerUnit, icon: FiZap, color: 'text-[#c8f542]' },
                ].map(({ label, value, icon, color }) => (
                  <div key={label} className="bg-[#111318] rounded-xl p-3 text-center border border-[#2c313a]">
                    <SafeIcon icon={icon} className={`w-4 h-4 mx-auto mb-1 ${color}`} />
                    <p className={`text-sm font-black ${color}`}>{value}</p>
                    <p className="text-[9px] text-gray-500 uppercase tracking-wider mt-0.5">{label}</p>
                  </div>
                ))}
              </div>

              {/* Earnings */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#111318] rounded-xl p-3 border border-[#2c313a]">
                  <div className="flex items-center space-x-2 mb-1">
                    <SafeIcon icon={FiClock} className="w-3.5 h-3.5 text-gray-400" />
                    <p className="text-[10px] text-gray-400 uppercase tracking-widest">Weekly</p>
                  </div>
                  <p className="text-sm font-black text-white">{idea.weeklyProfit}</p>
                  <p className="text-[10px] text-gray-500">{idea.weeklySales}</p>
                </div>
                <div className="bg-[#111318] rounded-xl p-3 border border-[#2c313a]">
                  <div className="flex items-center space-x-2 mb-1">
                    <SafeIcon icon={FiTrendingUp} className="w-3.5 h-3.5 text-[#c8f542]" />
                    <p className="text-[10px] text-gray-400 uppercase tracking-widest">Monthly</p>
                  </div>
                  <p className="text-sm font-black text-[#c8f542]">{idea.monthlyProfit}</p>
                </div>
              </div>

              {/* First Mission */}
              <div className="bg-[#c8f542]/10 border border-[#c8f542]/30 rounded-xl p-3 flex items-start space-x-3">
                <SafeIcon icon={FiTarget} className="w-4 h-4 text-[#c8f542] shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] font-black text-[#c8f542] uppercase tracking-widest mb-1">🎯 First Mission</p>
                  <p className="text-xs text-gray-300 leading-relaxed">{idea.firstMission}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function HustlePlanResult({ plan }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3 mt-2"
    >
      <p className="text-gray-300 text-sm leading-relaxed">{plan.intro}</p>
      <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">
        Tap a card to see the full breakdown ↓
      </p>
      {plan.ideas.map((idea, i) => (
        <IdeaCard key={i} idea={idea} isBest={idea.name === plan.bestOption} />
      ))}
      {plan.bestReason && (
        <div className="bg-[#1a1d23] border border-[#2c313a] rounded-2xl p-4">
          <p className="text-[10px] text-[#c8f542] font-black uppercase tracking-widest mb-1">⭐ Why {plan.bestOption} is your best bet</p>
          <p className="text-sm text-gray-300 leading-relaxed">{plan.bestReason}</p>
        </div>
      )}
      {plan.momentumQuestion && (
        <p className="text-sm text-gray-400 italic border-l-2 border-[#c8f542]/40 pl-3">{plan.momentumQuestion}</p>
      )}
    </motion.div>
  );
}

function ChatBubble({ message }) {
  const isUser = message.role === 'user';
  const plan = !isUser ? parseHustlePlan(message.content) : null;
  const displayText = plan ? getTextWithoutPlan(message.content) : message.content;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-start space-x-3 ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isUser ? 'bg-[#c8f542]' : 'bg-[#1e2228] border border-[#2c313a]'}`}>
        {isUser
          ? <SafeIcon icon={FiUser} className="w-4 h-4 text-black" />
          : <SafeIcon icon={FiCpu} className="w-4 h-4 text-[#c8f542]" />
        }
      </div>
      <div className={`max-w-[90%] ${isUser ? '' : 'w-full'}`}>
        {isUser ? (
          <div className="bg-[#c8f542] text-black font-semibold rounded-2xl rounded-tr-sm px-4 py-3 text-sm">
            <p className="text-black">{message.content}</p>
          </div>
        ) : (
          <div className="bg-[#1a1d23] border border-[#2c313a] text-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 text-sm">
            {displayText && <div className="space-y-0.5">{formatMessage(displayText)}</div>}
            {plan && <HustlePlanResult plan={plan} />}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function TypingIndicator() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center space-x-3">
      <div className="w-8 h-8 rounded-full bg-[#1e2228] border border-[#2c313a] flex items-center justify-center shrink-0">
        <SafeIcon icon={FiCpu} className="w-4 h-4 text-[#c8f542]" />
      </div>
      <div className="bg-[#1a1d23] border border-[#2c313a] rounded-2xl rounded-tl-sm px-4 py-3">
        <div className="flex space-x-1.5">
          {[0, 1, 2].map(i => (
            <motion.div key={i} className="w-2 h-2 bg-[#c8f542]/60 rounded-full"
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function DiscoverSection({ onEnterApp }) {
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatScrollRef = useRef(null);

  // Only auto-scroll the chat container, not the page
  const scrollChatToBottom = useCallback(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollChatToBottom();
  }, [messages, loading, scrollChatToBottom]);

  const handleSend = async (overrideText) => {
    const text = (overrideText || input).trim();
    if (!text || loading) return;

    setInput('');
    setError('');
    const userMessage = { role: 'user', content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setLoading(true);

    try {
      const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
      if (!apiKey) throw new Error('OpenAI API key not configured.');

      const apiMessages = [
        { role: 'system', content: SYSTEM_PROMPT },
        ...newMessages.map(m => ({ role: m.role, content: m.content })),
      ];

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({ model: 'gpt-4o-mini', messages: apiMessages, max_tokens: 1200, temperature: 0.7 }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error?.message || `API error ${response.status}`);
      }

      const data = await response.json();
      const reply = data.choices?.[0]?.message?.content?.trim();
      if (!reply) throw new Error('No response from AI.');
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleReset = () => {
    setMessages([INITIAL_MESSAGE]);
    setInput('');
    setError('');
    setLoading(false);
  };

  const quickReplies = messages.length === 1
    ? ['🟢 Beginner', '🟡 Basic', '🟠 Intermediate', '🔴 Advanced']
    : [];

  return (
    <section id="discover-sidehustle" className="relative py-24 px-6 bg-[#060608] overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#c8f542]/5 rounded-full blur-3xl" />
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[250px] h-[250px] bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
          <div className="inline-flex items-center space-x-2 border border-[#c8f542]/20 bg-[#c8f542]/5 text-[#c8f542] text-xs font-black px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
            <SafeIcon icon={FiShoppingBag} className="w-3 h-3" />
            <span>AI Side Hustle Coach</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
            What can you{' '}
            <span className="text-[#c8f542] relative">
              make & sell?
              <motion.span className="absolute inset-0 blur-xl opacity-20 bg-[#c8f542]"
                animate={{ opacity: [0.1, 0.3, 0.1] }} transition={{ duration: 3, repeat: Infinity }} />
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto leading-relaxed">
            Chat with your personal SideHustlr Coach. Answer 4 quick questions and get a real, personalised hustle plan.
          </p>
        </motion.div>

        {/* Chat Window — fixed height, internal scroll only */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-[#0e1014] border border-[#2c313a] rounded-3xl overflow-hidden flex flex-col"
          style={{ height: 580 }}
        >
          {/* Chat Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#2c313a] bg-[#111318] shrink-0">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-9 h-9 bg-[#c8f542]/15 border border-[#c8f542]/30 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiCpu} className="w-4 h-4 text-[#c8f542]" />
                </div>
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 border-2 border-[#111318] rounded-full" />
              </div>
              <div>
                <p className="text-sm font-black text-white">SideHustlr Coach</p>
                <p className="text-[10px] text-green-400 font-bold uppercase tracking-wider">Online — ready to help</p>
              </div>
            </div>
            <button onClick={handleReset} title="Start over"
              className="flex items-center space-x-1.5 px-3 py-1.5 bg-[#1e2228] hover:bg-[#2c313a] border border-[#2c313a] text-gray-400 hover:text-white rounded-xl transition-all text-xs font-bold">
              <SafeIcon icon={FiRefreshCw} className="w-3 h-3" />
              <span>Reset</span>
            </button>
          </div>

          {/* Messages — scrollable area */}
          <div
            ref={chatScrollRef}
            className="flex-1 overflow-y-auto p-5 space-y-4 custom-scrollbar bg-[#0e1014]"
          >
            {messages.map((msg, i) => (
              <ChatBubble key={i} message={msg} />
            ))}
            {loading && <TypingIndicator />}
            <div ref={chatEndRef} />
          </div>

          {/* Quick Replies */}
          <AnimatePresence>
            {quickReplies.length > 0 && !loading && (
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="px-5 pb-3 pt-2 flex flex-wrap gap-2 bg-[#0e1014] shrink-0 border-t border-[#1e2228]"
              >
                {quickReplies.map(r => (
                  <button key={r} onClick={() => handleSend(r)}
                    className="px-4 py-2 bg-[#1a1d23] border border-[#2c313a] hover:border-[#c8f542]/40 hover:bg-[#c8f542]/10 text-gray-300 hover:text-[#c8f542] rounded-xl text-xs font-bold transition-all">
                    {r}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error */}
          {error && (
            <div className="mx-5 mb-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-xs font-medium shrink-0">
              {error}
            </div>
          )}

          {/* Input */}
          <div className="px-5 pb-4 bg-[#0e1014] border-t border-[#2c313a] pt-3 shrink-0">
            <div className="flex space-x-3">
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your answer..."
                disabled={loading}
                className="flex-1 bg-[#1a1d23] border border-[#2c313a] focus:border-[#c8f542]/50 focus:ring-2 focus:ring-[#c8f542]/10 rounded-2xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition-all disabled:opacity-50"
              />
              <button
                onClick={() => handleSend()}
                disabled={loading || !input.trim()}
                className="px-5 py-3 bg-[#c8f542] hover:bg-[#d4ff50] text-black rounded-2xl font-black text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-2 hover:scale-105"
              >
                {loading
                  ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                  : <SafeIcon icon={FiSend} className="w-4 h-4" />
                }
              </button>
            </div>
            <p className="text-center text-[10px] text-gray-700 mt-2 font-medium">
              Powered by AI · Personalised for South Africa · Takes 2 minutes
            </p>
          </div>
        </motion.div>

        {/* CTA below chat */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-8 bg-gradient-to-r from-[#c8f542]/10 to-purple-500/10 border border-[#c8f542]/20 rounded-3xl p-8 text-center"
        >
          <div className="flex items-center justify-center space-x-2 mb-3">
            <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-[#c8f542]" />
            <h3 className="text-2xl font-black text-white">Ready to start selling?</h3>
          </div>
          <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
            Build your own online store on SideHustlr and start selling your products to South Africa — no tech skills needed.
          </p>
          <button onClick={onEnterApp}
            className="group relative bg-[#c8f542] text-black font-black px-10 py-4 rounded-full transition-all duration-300 hover:bg-[#d4ff50] hover:scale-105 hover:shadow-[0_0_40px_12px_rgba(200,245,66,0.4)] inline-flex items-center space-x-2 overflow-hidden">
            <span className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 pointer-events-none" />
            <SafeIcon icon={FiShoppingBag} className="relative z-10 w-5 h-5" />
            <span className="relative z-10">Build My Online Store — Free</span>
            <SafeIcon icon={FiArrowRight} className="relative z-10 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}